import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import { useIsAdmin } from "./hooks/useIsAdmin";
import { BottomNav } from "./components/BottomNav";
import { TopBar } from "./components/TopBar";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { ClubSelect } from "./pages/ClubSelect";
import { ClubDashboard } from "./pages/ClubDashboard";
import { ClubInfo } from "./pages/ClubInfo";
import { NewsDetail } from "./pages/NewsDetail";
import { EventDetail } from "./pages/EventDetail";
import { ReportIssue } from "./pages/ReportIssue";
import { AccountDelete } from "./pages/AccountDelete";
import { BallFlightAnalysis } from "./pages/BallFlightAnalysis";
import { AdminLogin } from "./pages/AdminLogin";
import { AdminDashboard } from "./pages/AdminDashboard";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, initializing } = useAuth();
  const location = useLocation();

  if (initializing) {
    return (
      <div className="flex min-h-dvh items-center justify-center text-ink-400">
        Wird geladen…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user, initializing } = useAuth();
  const isAdmin = useIsAdmin();
  const location = useLocation();

  if (initializing || isAdmin === null) {
    return (
      <div className="flex min-h-dvh items-center justify-center text-ink-400">
        Wird geladen…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }
  if (!isAdmin) {
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center px-6 text-center">
        <p className="font-display text-xl font-semibold text-turf-900">Kein Admin-Zugriff</p>
        <p className="mt-2 text-sm text-ink-600">
          Dieser Account ist nicht als Admin eingetragen. Bitte in Firestore unter "admins" hinzufügen lassen.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <>
      <TopBarGate />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route
          path="/admin"
          element={
            <RequireAdmin>
              <AdminDashboard />
            </RequireAdmin>
          }
        />
        <Route path="/" element={<Navigate to="/clubs" replace />} />
        <Route
          path="/clubs"
          element={
            <RequireAuth>
              <ClubSelect />
            </RequireAuth>
          }
        />
        <Route
          path="/club/:clubId"
          element={
            <RequireAuth>
              <ClubDashboard />
            </RequireAuth>
          }
        />
        <Route
          path="/club/:clubId/info"
          element={
            <RequireAuth>
              <ClubInfo />
            </RequireAuth>
          }
        />
        <Route
          path="/club/:clubId/news/:noticeId"
          element={
            <RequireAuth>
              <NewsDetail />
            </RequireAuth>
          }
        />
        <Route
          path="/club/:clubId/events/:eventId"
          element={
            <RequireAuth>
              <EventDetail />
            </RequireAuth>
          }
        />
        <Route
          path="/club/:clubId/melden"
          element={
            <RequireAuth>
              <ReportIssue />
            </RequireAuth>
          }
        />
        <Route
          path="/club/:clubId/ballflug"
          element={
            <RequireAuth>
              <BallFlightAnalysis />
            </RequireAuth>
          }
        />
        <Route
          path="/account/delete"
          element={
            <RequireAuth>
              <AccountDelete />
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/clubs" replace />} />
      </Routes>
      <BottomNavGate />
    </>
  );
}

function BottomNavGate() {
  const location = useLocation();
  const match = location.pathname.match(/^\/club\/([^/]+)/);
  if (!match) return null;
  return <BottomNav clubId={match[1]} />;
}

function TopBarGate() {
  const location = useLocation();
  const showsOn = location.pathname === "/clubs" || location.pathname.startsWith("/club/");
  if (showsOn) return <TopBar />;
  return null;
}
